import { GridState } from "@ag-grid-community/core";
import { Flex, Text, Tooltip } from "@chakra-ui/react";
import {
  CAPITAL_AMOUNT_FIELD,
  STUB_CALCULATION,
} from "features/transactions/TransactionsViewTable/CalculationActions/constants";
import { useTransactionCalculationList } from "gen/endpoints/transactions/transactions";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  SelectGroup,
  SelectOption,
} from "shared/components/input/select/SelectOption";
import { SimpleSelect } from "shared/components/input/select/SimpleSelect";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";

export const TransactionCalculationDropdown: React.FC<
  {
    fundFamilyIds: string[];
    transactionCalculationId: string | null | undefined;
    setCalculationId: (calcId: string) => void;
    initialState: GridState;
  } & Pick<React.ComponentProps<SimpleSelect>, "isDisabled">
> = ({
  initialState,
  fundFamilyIds,
  transactionCalculationId,
  setCalculationId,
  isDisabled,
}) => {
  const fundFamilyId = fundFamilyIds.length === 1 ? fundFamilyIds[0] : "";
  const { t } = useTranslation(["enums", "views"]);
  const { data: calculations, isLoading } = useTransactionCalculationList(
    fundFamilyId,
    {},
    {
      query: {
        enabled: !!fundFamilyId,
      },
    },
  );
  const { agGrid } = useMaybernTableUIContext();

  const setCalcInPivotSettings = useCallback(
    (calcId: string) => {
      agGrid?.api.setValueColumns([CAPITAL_AMOUNT_FIELD]);
      agGrid?.api.setColumnAggFunc(CAPITAL_AMOUNT_FIELD, calcId);
    },
    [agGrid],
  );

  // only do this to default to the stub calculation
  // but if there is an existing aggregation model, don't override it
  useEffect(() => {
    if (initialState.aggregation?.aggregationModel) return;
    setCalcInPivotSettings(STUB_CALCULATION.id);
  }, [initialState.aggregation?.aggregationModel, setCalcInPivotSettings]);

  const body = (
    <Flex display="flex" mb="0" alignItems="center">
      <Text minWidth="max-content" textStyle="label" mr="8px">
        {t("views:aggregateBy")}
      </Text>

      <SimpleSelect
        size="sm"
        title={t("enums:FundFamilyEventSubTypeEnum.transaction_calculation")}
        value={transactionCalculationId}
        onChangeValue={(val) => {
          setCalcInPivotSettings(val);
          setCalculationId(val);
        }}
        isDisabled={isLoading || !fundFamilyId || isDisabled}
        minWidth="275px"
        placeholder={t("views:selectCalcPlaceholder")}
      >
        <SelectGroup label="">
          <SelectOption key={STUB_CALCULATION.id} value={STUB_CALCULATION.id}>
            {STUB_CALCULATION.name}
          </SelectOption>
        </SelectGroup>
        <SelectGroup label={t("views:calculations")}>
          {(calculations || [])
            .filter((calc) => calc.calculation_type === "basic")
            .map(({ id, name }) => (
              <SelectOption
                labelStyles={{
                  overflow: "hidden",
                  width: "200px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                key={id}
                value={id}
              >
                {name}
              </SelectOption>
            ))}
        </SelectGroup>
      </SimpleSelect>
    </Flex>
  );
  if (isDisabled) {
    return (
      <Tooltip label={t("views:calculationDisabledTooltip")}>{body}</Tooltip>
    );
  }
  return body;
};
